import { useEffect, useState } from 'react';
import { DatePicker, Form, Input, Select, Row, Col, FormInstance, Radio, RadioChangeEvent } from 'antd';
import { DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ClientService } from '../../../../shared/api/ClientService';
import API from '../../../../utils/api';
import useLocale from '../../../../hooks/useLocale';
import SelectSuffixIcon from '../../../../components/SelectSuffixIcon/SelectSuffixIcon';

import './ApplicationFormTab.scss';
import { DATE_FORMAT2, DATE_FORMAT_PLACEHOLDER } from '../../../../constants/common';
import { antdUtil } from '../../../../utils/antdUtil';
import ChangeTrackerFormItem from '../../../../components/ChangeTrackerFormItem';
import SearchLocationInput from '../../../../components/SearchLocationInput/SearchLocationInput';
import { LocationDto } from '../../../../modals/GoogleMaps';
import { R27_FEATUREFLAG } from '../../../../components/FeatureFlag';

interface IProps {
  form: FormInstance<ClientService.AppFormPersonalInfoDto>;
  name: string;
  rowName: number;
  onAddressAutocomplete: (rowName: number) => (address: LocationDto) => void;
  remove: (index: number | number[]) => void;
  isLocked?: boolean;
  listName?: string;
}

const PersonalInformationPreviousInsolvencyItem = ({
  form,
  name,
  rowName,
  onAddressAutocomplete,
  remove,
  isLocked = false,
  listName,
}: IProps): JSX.Element => {
  const { Option } = Select;
  const { t } = useLocale();
  const [insolvencyTypes, setInsolvencyTypes] = useState<ClientService.LookupDto[]>();
  const [insolvencyTypesLoading, setInsolvencyTypesLoading] = useState<boolean>(false);

  const [proposalResults, setProposalResults] = useState<ClientService.LookupDto[]>();
  const [proposalResultsLoading, setProposalResultsLoading] = useState<boolean>(false);

  const [bankruptcyDischargeTypes, setBankruptcyDischargeTypes] = useState<ClientService.LookupDto[]>();
  const [bankruptcyDischargeTypesLoading, setBankruptcyDischargeTypesLoading] = useState<boolean>(false);

  const [isPreviousInsolvencyBankruptcy, setIsPreviousInsolvencyBankruptcy] = useState<boolean>(false);
  const [isPreviousInsolvencyProposal, setIsPreviousInsolvencyProposal] = useState<boolean>(false);

  useEffect(() => {
    setInsolvencyTypesLoading(true);
    API.listInsolvencyTypes().then((response) => {
      setInsolvencyTypes(response);
      setInsolvencyTypesLoading(false);
    });

    setProposalResultsLoading(true);
    API.listProposalResults().then((response) => {
      setProposalResults(response);
      setProposalResultsLoading(false);
    });

    setBankruptcyDischargeTypesLoading(true);
    API.listBankruptcyDischargeTypes().then((response) => {
      setBankruptcyDischargeTypes(response);
      setBankruptcyDischargeTypesLoading(false);
    });
  }, []);

  useEffect(() => {
    let typeId = form.getFieldValue([name, rowName, 'insolvencyTypeId']);
    let selType = insolvencyTypes?.find((item) => item?.id === typeId);

    setIsPreviousInsolvencyBankruptcy(selType?.enumValue === ClientService.InsolvencyTypeEnum.Bankruptcy);
    setIsPreviousInsolvencyProposal(selType?.enumValue === ClientService.InsolvencyTypeEnum.Proposal);
  }, [form, insolvencyTypes, name, rowName]);

  const handlePreviousInsolvencyTypeChanged = (e: RadioChangeEvent) => {
    const selType = insolvencyTypes?.find((item) => item?.id === e?.target?.value);

    setIsPreviousInsolvencyBankruptcy(selType?.enumValue === ClientService.InsolvencyTypeEnum.Bankruptcy);
    setIsPreviousInsolvencyProposal(selType?.enumValue === ClientService.InsolvencyTypeEnum.Proposal);
  };

  return (
    <>
      <Form.Item name={[rowName, 'id']} hidden>
        <Input disabled={isLocked} />
      </Form.Item>
      <Row gutter={20}>
        <Col span={8}>
          <ChangeTrackerFormItem
            name={[rowName, 'insolvencyTypeId']}
            label={t.TYPE}
            rules={[{ required: true }]}
            listName={listName}
          >
            <Radio.Group
              onChange={handlePreviousInsolvencyTypeChanged}
              buttonStyle="solid"
              optionType="button"
              disabled={isLocked}
            >
              {insolvencyTypes?.map((option) => (
                <Radio.Button key={option.id} value={option?.id}>
                  {option?.name}
                </Radio.Button>
              ))}
            </Radio.Group>
          </ChangeTrackerFormItem>
        </Col>
        <Col span={16} />
        <Col span={8}>
          <ChangeTrackerFormItem
            name={[rowName, 'applicantName']}
            label={t.UNDER_WHAT_NAME_DID_YOU_FILE}
            listName={listName}
          >
            <Input size="large" disabled={isLocked} />
          </ChangeTrackerFormItem>
        </Col>
        <Col span={8}>
          <ChangeTrackerFormItem name={[rowName, 'trusteeName']} label={t.TRUSTEES_NAME} listName={listName}>
            <Input size="large" disabled={isLocked} />
          </ChangeTrackerFormItem>
        </Col>
        <Col span={8}>
          <ChangeTrackerFormItem name={[rowName, 'estateNumber']} label={t.ESTATE_NUMBER} listName={listName}>
            <Input size="large" disabled={isLocked} />
          </ChangeTrackerFormItem>
        </Col>
      </Row>

      <Row gutter={20} align="middle">
        {!R27_FEATUREFLAG && (
          <>
            <Col span={8}>
              <ChangeTrackerFormItem name={[rowName, 'filingLocation']} label={t.PLACE_FILED} listName={listName}>
                <Input size="large" disabled={true} />
              </ChangeTrackerFormItem>
            </Col>
          </>
        )}
        <Col span={8} hidden={isPreviousInsolvencyProposal !== true}>
          <ChangeTrackerFormItem
            name={[rowName, 'proposalResultId']}
            className="ApplicationFormTab__input-label-warning"
            label={t.PROPOSAL_RESULT}
            listName={listName}
            rules={[{ required: true }]}
          >
            <Select loading={proposalResultsLoading} suffixIcon={<SelectSuffixIcon />} size="large" disabled={isLocked}>
              {proposalResults?.map((option) => (
                <Option key={option.id} value={option?.id}>
                  {option?.name}
                </Option>
              ))}
            </Select>
          </ChangeTrackerFormItem>
        </Col>
        <Col span={R27_FEATUREFLAG ? 8 : 7} hidden={isPreviousInsolvencyProposal !== true}>
          <ChangeTrackerFormItem
            name={[rowName, 'proposalDate']}
            className="ApplicationFormTab__input-label-warning"
            label={t.PROPOSAL_DATE}
            listName={listName}
            rules={[{ required: true }]}
          >
            <DatePicker
              size="large"
              disabledDate={antdUtil.futureDates}
              format={DATE_FORMAT2}
              placeholder={DATE_FORMAT_PLACEHOLDER}
              disabled={isLocked}
            />
          </ChangeTrackerFormItem>
        </Col>
        <Col span={8} hidden={R27_FEATUREFLAG && isPreviousInsolvencyProposal !== true}>
          <ChangeTrackerFormItem
            name={[rowName, 'proposalDischargeDate']}
            className="ApplicationFormTab__input-label-warning"
            label={t.PROPOSAL_DISCHARGE_DATE}
            listName={listName}
            rules={[{ required: true }]}
          >
            <DatePicker
              size="large"
              disabledDate={antdUtil.futureDates}
              format={DATE_FORMAT2}
              placeholder={DATE_FORMAT_PLACEHOLDER}
              disabled={isLocked}
            />
          </ChangeTrackerFormItem>
        </Col>
        <Col span={8} hidden={isPreviousInsolvencyBankruptcy !== true}>
          <ChangeTrackerFormItem
            name={[rowName, 'bankruptcyDischargeTypeId']}
            className="ApplicationFormTab__input-label-warning"
            label={t.BANKRUPTCY_DISCHARGE_TYPE}
            listName={listName}
            rules={[{ required: true }]}
          >
            <Select
              loading={bankruptcyDischargeTypesLoading}
              suffixIcon={<SelectSuffixIcon />}
              size="large"
              disabled={isLocked}
            >
              {bankruptcyDischargeTypes?.map((option) => (
                <Option key={option.id} value={option?.id}>
                  {option?.name}
                </Option>
              ))}
            </Select>
          </ChangeTrackerFormItem>
        </Col>
        <Col span={8} hidden={isPreviousInsolvencyBankruptcy !== true}>
          <ChangeTrackerFormItem
            name={[rowName, 'dischargeDate']}
            className="ApplicationFormTab__input-label-warning"
            label={t.BANKRUPTCY_DISCHARGE_DATE}
            listName={listName}
            rules={[{ required: true }]}
          >
            <DatePicker
              size="large"
              disabledDate={antdUtil.futureDates}
              format={DATE_FORMAT2}
              placeholder={DATE_FORMAT_PLACEHOLDER}
              disabled={isLocked}
            />
          </ChangeTrackerFormItem>
        </Col>
        <Col span={7} hidden={isPreviousInsolvencyBankruptcy !== true}>
          <ChangeTrackerFormItem
            listName={listName}
            name={[rowName, 'bankruptcyDate']}
            className="ApplicationFormTab__input-label-warning"
            label={t.BANKRUPTCY_DATE}
            rules={[{ required: true }]}
          >
            <DatePicker
              size="large"
              disabledDate={antdUtil.futureDates}
              format={DATE_FORMAT2}
              placeholder={DATE_FORMAT_PLACEHOLDER}
              disabled={isLocked}
            />
          </ChangeTrackerFormItem>
        </Col>
        {R27_FEATUREFLAG && (
          <>
            <Col span={8}>
              <ChangeTrackerFormItem name={[rowName, 'filingLocation']} label={t.PLACE_FILED} listName={listName}>
                <Input size="large" disabled={true} />
              </ChangeTrackerFormItem>
            </Col>
          </>
        )}
        {!isLocked && (
          <Col span={1} style={{ textAlign: 'left' }}>
            <DeleteOutlined className="ApplicationFormTab__input-action-icon" onClick={() => remove(rowName)} />
          </Col>
        )}
        <Col span={16}>
          <Form.Item noStyle>
            <div className="ApplicationFormTab__link-left">
              <InfoCircleOutlined className="ApplicationFormTab__redundant-info-icon" />
              {t.INSOLVENCY_REDUNDANT_FIELD}
            </div>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={20} align="middle">
        <Col span={16}>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues[rowName] !== currentValues[rowName]}
          >
            {({ getFieldValue }) => (
              <ChangeTrackerFormItem name={[rowName, 'address']} label={t.INSOLVENCY_ADDRESS} listName={listName}>
                <SearchLocationInput
                  address={{ address: getFieldValue([rowName, 'address']) }}
                  onSelect={onAddressAutocomplete(rowName)}
                  size="large"
                  disabled={isLocked}
                />
              </ChangeTrackerFormItem>
            )}
          </Form.Item>
        </Col>
        <Col span={8}>
          <ChangeTrackerFormItem name={[rowName, 'unit']} label={t.INSOLVENCY_ADDRESS_UNIT} listName={listName}>
            <Input size="large" disabled={isLocked} />
          </ChangeTrackerFormItem>
        </Col>
      </Row>
    </>
  );
};

export default PersonalInformationPreviousInsolvencyItem;
